import { html, css, LitElement } from 'lit-element';
import {
    ref, uploadBytes, getDownloadURL, deleteObject
} from 'firebase/storage';
import {
    collection, addDoc, setDoc, getDocs, deleteDoc, doc, query, orderBy
} from 'firebase/firestore';

import '@material/mwc-button';
import '@material/mwc-textfield';
import '@material/mwc-textarea';
import '@material/mwc-select';
import '@material/mwc-list/mwc-list-item';

import { db, storage } from './intrivity_app.js';
import { hasRole } from './utility.js';
import { a11yStyles, disclosureButton, uniqueId } from './a11y.js';
import { t, Localized, formatDate, formatNumber } from './i18n.js';

// Compliance Documentation -- a SuperAdmin-only store for the evidence a
// higher-ed customer asks for during a vendor review: the completed HECVAT
// workbook itself plus the policies, audit reports, accessibility conformance
// report and architecture notes it refers out to.
//
// Named for documentation rather than security because the scope is broader
// than security alone: accessibility conformance (VPAT/ACR), privacy and data
// retention, and subprocessor disclosure all live here too, and a reviewer
// asks for them in the same breath.
//
// Files live in Cloud Storage under compliance/{docId}/{filename}; the metadata
// (title, category, notes, who uploaded it and when) lives in the Firestore
// `compliance_documents` collection. The two are kept in step by deleting the
// Storage object first and the metadata second, so a failure leaves an orphaned
// row that is visible in the UI rather than a row pointing at a missing file.
//
// The categories below are deliberately version-agnostic: HECVAT Full, Lite and
// 4.0 ask for the same underlying evidence even though they group it differently.
// `label` reads the current language each time (compliance.category.<id>).
export const COMPLIANCE_CATEGORIES = ['hecvat', 'policy', 'audit', 'architecture', 'subprocessors', 'accessibility', 'legal', 'other']
    .map((id) => ({ id, get label() { return t(`compliance.category.${id}`); } }));

const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export class IntrivityAdminCompliance extends Localized(LitElement) {

    static get styles() {
        return [a11yStyles, css`
            /* Matches the CLIENTS / CONFIGURATION panel headers. */
            .section-header {
                margin: 0 0 5px 0;
                width: 100%;
                box-sizing: border-box;
                color: black;
                font-size: 1.5em;
                font-weight: normal;
                padding: 15px 10px 10px 30px;
                background-color: gray;
            }
            .section-header .disclosure {
                vertical-align: middle;
                margin-left: 8px;
                padding: 4px;
            }
            .body {
                padding: 10px 30px 30px;
            }
            .intro {
                max-width: 70ch;
                color: #444;
                line-height: 1.55;
            }
            h3 {
                font-size: 1.1em;
                font-weight: 600;
                margin: 24px 0 8px;
            }
            .upload {
                border: 1px solid #d8d8d8;
                border-radius: 6px;
                padding: 16px 18px;
                background: #fafafa;
                max-width: 720px;
                display: grid;
                gap: 12px;
            }
            .upload .row {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 12px;
            }
            .file-row {
                display: flex;
                flex-wrap: wrap;
                align-items: center;
                gap: 10px;
            }
            .file-row label {
                font-weight: 600;
            }
            mwc-textfield, mwc-textarea, mwc-select {
                width: 100%;
            }
            .actions {
                display: flex;
                gap: 10px;
                align-items: center;
            }
            .status {
                min-height: 1.4em;
                font-size: 0.9em;
                color: #1a6b1a;
            }
            .status.error {
                color: #a3341f;
            }
            .table-scroll {
                overflow-x: auto;
                max-width: 100%;
            }
            table {
                border-collapse: collapse;
                width: 100%;
                min-width: 620px;
                font-size: 0.92em;
                margin-top: 6px;
            }
            caption {
                text-align: left;
                padding: 6px 0;
                color: #5c5c5c;
            }
            th, td {
                border-bottom: 1px solid #e4e4e4;
                padding: 9px 10px;
                text-align: left;
                vertical-align: top;
            }
            thead th {
                border-bottom: 1px solid #bbb;
                font-size: 0.82em;
                letter-spacing: 0.04em;
                text-transform: uppercase;
                color: #5c5c5c;
                font-weight: 600;
            }
            td.meta {
                color: #6b6b6b;
                white-space: nowrap;
            }
            td.doc-actions {
                white-space: nowrap;
                text-align: right;
            }
            .empty {
                color: #6b6b6b;
                padding: 14px 0;
            }
        `];
    }

    static get properties() {
        return {
            user_profile: { type: Object },
            expanded: { type: Boolean },
            _docs: { type: Array },
            _loading: { type: Boolean },
            _busy: { type: Boolean },
            _status: { type: String },
            _error: { type: String },
            _title: { type: String },
            _category: { type: String },
            _notes: { type: String },
        };
    }

    constructor() {
        super();
        this._panelId = uniqueId('compliance-documentation');
        this._fileInputId = uniqueId('compliance-file');
        this.expanded = false;
        this._docs = [];
        this._loading = false;
        this._busy = false;
        this._status = '';
        this._error = '';
        this._title = '';
        this._category = 'hecvat';
        this._notes = '';
    }

    updated(changed) {
        // Only hit Firestore once the panel is actually opened.
        if (changed.has('expanded') && this.expanded && !this._loading && this._docs.length === 0) {
            this._loadDocs();
        }
    }

    async _loadDocs() {
        this._loading = true;
        this._error = '';
        try {
            const snap = await getDocs(query(collection(db, 'compliance_documents'), orderBy('uploadedAt', 'desc')));
            this._docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e) {
            this._error = t('compliance.loadError', { message: e.message || e });
        } finally {
            this._loading = false;
        }
    }

    get _fileInput() {
        return this.shadowRoot.getElementById(this._fileInputId);
    }

    async _upload() {
        this._status = '';
        this._error = '';

        const input = this._fileInput;
        const file = input && input.files && input.files[0];
        if (!file) {
            this._error = t('compliance.chooseFile');
            return;
        }
        if (!this._title.trim()) {
            this._error = t('compliance.needTitle');
            return;
        }
        if (file.size > MAX_UPLOAD_BYTES) {
            this._error = t('compliance.tooLarge', { size: this._formatSize(file.size), limit: this._formatSize(MAX_UPLOAD_BYTES) });
            return;
        }

        this._busy = true;
        try {
            // Metadata row first, so its id can name the storage folder and a
            // failed upload leaves a row that is obviously incomplete rather
            // than a file nothing points at.
            const meta = {
                title: this._title.trim(),
                category: this._category,
                notes: this._notes.trim(),
                filename: file.name,
                contentType: file.type || 'application/octet-stream',
                size: file.size,
                uploadedBy: (this.user_profile && this.user_profile.email) || 'unknown',
                uploadedAt: new Date().toISOString(),
                storagePath: '',
            };
            const created = await addDoc(collection(db, 'compliance_documents'), meta);
            const storagePath = `compliance/${created.id}/${file.name}`;
            await uploadBytes(ref(storage, storagePath), file, { contentType: meta.contentType });
            meta.storagePath = storagePath;
            await setDoc(doc(db, 'compliance_documents', created.id), meta, { merge: true });

            this._status = t('compliance.uploaded', { title: meta.title });
            this._title = '';
            this._notes = '';
            if (input) input.value = '';
            this._docs = [];
            await this._loadDocs();
        } catch (e) {
            this._error = t('compliance.uploadFailed', { message: e.message || e });
        } finally {
            this._busy = false;
        }
    }

    async _download(entry) {
        this._status = '';
        this._error = '';
        if (!entry.storagePath) {
            this._error = t('compliance.noFile', { title: entry.title });
            return;
        }
        try {
            const url = await getDownloadURL(ref(storage, entry.storagePath));
            window.open(url, '_blank', 'noopener');
        } catch (e) {
            this._error = t('compliance.openFailed', { message: e.message || e });
        }
    }

    async _delete(entry) {
        if (!confirm(t('compliance.confirmDelete', { title: entry.title }))) return;
        this._status = '';
        this._error = '';
        this._busy = true;
        try {
            if (entry.storagePath) {
                await deleteObject(ref(storage, entry.storagePath)).catch((e) => {
                    // A missing object shouldn't block removing the stale row.
                    if (e && e.code !== 'storage/object-not-found') throw e;
                });
            }
            await deleteDoc(doc(db, 'compliance_documents', entry.id));
            this._status = t('compliance.deleted', { title: entry.title });
            this._docs = this._docs.filter((d) => d.id !== entry.id);
        } catch (e) {
            this._error = t('compliance.deleteFailed', { message: e.message || e });
        } finally {
            this._busy = false;
        }
    }

    _formatSize(bytes) {
        if (!bytes && bytes !== 0) return '';
        if (bytes < 1024) return formatNumber(bytes, { style: 'unit', unit: 'byte', unitDisplay: 'narrow' });
        if (bytes < 1024 * 1024) return formatNumber(Math.round(bytes / 1024), { style: 'unit', unit: 'kilobyte', unitDisplay: 'short' });
        return formatNumber(bytes / (1024 * 1024), { style: 'unit', unit: 'megabyte', unitDisplay: 'short', maximumFractionDigits: 1 });
    }

    _categoryLabel(id) {
        const found = COMPLIANCE_CATEGORIES.find((c) => c.id === id);
        return found ? found.label : id;
    }

    _formatDate(iso) {
        if (!iso) return '';
        const d = new Date(iso);
        return isNaN(d) ? '' : formatDate(d, { dateStyle: 'short' });
    }

    _renderUpload() {
        return html`
            <h3>${t('compliance.addHeading')}</h3>
            <div class="upload">
                <div class="file-row">
                    <label for=${this._fileInputId}>${t('compliance.file')}</label>
                    <input type="file" id=${this._fileInputId} ?disabled=${this._busy}
                           accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.html,.htm,.png,.jpg,.jpeg" />
                    <span class="meta">${t('compliance.maximum', { limit: this._formatSize(MAX_UPLOAD_BYTES) })}</span>
                </div>

                <mwc-textfield
                    label=${t('compliance.titleLabel')}
                    required
                    .value=${this._title}
                    ?disabled=${this._busy}
                    @input=${(e) => this._title = e.target.value}></mwc-textfield>

                <div class="row">
                    <mwc-select
                        label=${t('compliance.colCategory')}
                        .value=${this._category}
                        ?disabled=${this._busy}
                        @selected=${(e) => {
                            const picked = COMPLIANCE_CATEGORIES[e.detail.index];
                            if (picked) this._category = picked.id;
                        }}
                        @closed=${(e) => e.stopPropagation()}>
                        ${COMPLIANCE_CATEGORIES.map((c) => html`
                            <mwc-list-item value=${c.id} ?selected=${c.id === this._category}>${c.label}</mwc-list-item>
                        `)}
                    </mwc-select>
                    <mwc-textfield
                        label=${t('compliance.notes')}
                        .value=${this._notes}
                        ?disabled=${this._busy}
                        @input=${(e) => this._notes = e.target.value}></mwc-textfield>
                </div>

                <div class="actions">
                    <mwc-button raised
                        label=${this._busy ? t('compliance.uploading') : t('compliance.upload')}
                        ?disabled=${this._busy}
                        @click=${() => this._upload()}></mwc-button>
                </div>
            </div>
        `;
    }

    _renderTable() {
        if (this._loading) {
            return html`<p class="empty" role="status">${t('compliance.loadingDocs')}</p>`;
        }
        if (!this._docs.length) {
            return html`<p class="empty">${t('compliance.none')}</p>`;
        }
        return html`
            <div class="table-scroll">
                <table>
                    <caption>${t('compliance.caption', { count: this._docs.length })}</caption>
                    <thead>
                        <tr>
                            <th scope="col">${t('compliance.colDocument')}</th>
                            <th scope="col">${t('compliance.colCategory')}</th>
                            <th scope="col">${t('compliance.colUploaded')}</th>
                            <th scope="col">${t('compliance.colSize')}</th>
                            <th scope="col"><span class="sr-only">${t('compliance.colActions')}</span></th>
                        </tr>
                    </thead>
                    <tbody>
                        ${this._docs.map((entry) => html`
                            <tr>
                                <th scope="row" style="font-weight: 600;">
                                    ${entry.title}
                                    ${entry.notes ? html`<div class="meta" style="font-weight: normal; white-space: normal;">${entry.notes}</div>` : html``}
                                    <div class="meta" style="font-weight: normal;">${entry.filename}</div>
                                </th>
                                <td>${this._categoryLabel(entry.category)}</td>
                                <td class="meta">
                                    ${this._formatDate(entry.uploadedAt)}<br />
                                    <span style="font-size: 0.9em;">${entry.uploadedBy}</span>
                                </td>
                                <td class="meta">${this._formatSize(entry.size)}</td>
                                <td class="doc-actions">
                                    <mwc-button dense outlined
                                        label=${t('compliance.open')}
                                        @click=${() => this._download(entry)}></mwc-button>
                                    <mwc-button dense outlined
                                        label=${t('common.delete')}
                                        ?disabled=${this._busy}
                                        @click=${() => this._delete(entry)}></mwc-button>
                                </td>
                            </tr>
                        `)}
                    </tbody>
                </table>
            </div>
        `;
    }

    render() {
        if (!hasRole('superadmin', this.user_profile)) return html``;

        return html`
            <h2 class="section-header">
                ${t('compliance.title')}
                ${disclosureButton(this.expanded, t('compliance.title'), this._panelId, () => { this.expanded = !this.expanded; })}
            </h2>

            ${this.expanded ? html`
                <div id=${this._panelId} class="body">
                    <p class="intro">
                        ${t('compliance.intro')}
                    </p>

                    <div class="status ${this._error ? 'error' : ''}" role="status" aria-live="polite">
                        ${this._error || this._status}
                    </div>

                    ${this._renderUpload()}

                    <h3>${t('compliance.stored')}</h3>
                    ${this._renderTable()}
                </div>
            `: html``}
        `;
    }
}

if (!customElements.get('intrivity-admin-compliance')) {
    customElements.define('intrivity-admin-compliance', IntrivityAdminCompliance);
}
